import { useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';

// Session storage keys
const AFFILIATE_SESSION_KEY = 'affiliate_session';
const AFFILIATE_DATA_KEY = 'affiliate_data';
const AFFILIATE_TRACKED_KEY = 'affiliate_tracked'; // NEW: Track if we've already sent tracking
const AFFILIATE_UTM_TRACKING_KEY = 'affiliate_utm_tracking'; // NEW: Track UTM parameter combinations

export interface AffiliateData {
	gameId?: string; // Changed from affiliateId to gameId
	referrerUserId?: string;
	sessionId: string;
	landingPage: string;
	utmSource?: string;
	utmMedium?: string;
	utmCampaign?: string;
	utmContent?: string;
	utmTerm?: string;
	timestamp: string;
	lastTracked?: string; // NEW: When we last sent a tracking call
	isTracked?: boolean; // NEW: Whether we've already tracked this session
}

/**
 * Generate a unique session ID
 */
export function generateSessionId(): string {
	return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Extract Game ID from hostname or URL parameters
 */
export function extractAffiliateId(): string | null {
	// Check subdomain first (e.g., alice.watchersociety.com)
	const hostname = window.location.hostname;
	if (
		hostname.endsWith('.watchersociety.com') &&
		hostname !== 'watchersociety.com'
	) {
		const subdomain = hostname.split('.')[0];
		if (subdomain && subdomain !== 'www') {
			return subdomain;
		}
	}

	// Check URL parameters as fallback
	const urlParams = new URLSearchParams(window.location.search);
	return (
		urlParams.get('ref') || urlParams.get('affiliate') || urlParams.get('a')
	);
}

/**
 * Extract UTM parameters from URL
 */
export function extractUtmParameters() {
	const urlParams = new URLSearchParams(window.location.search);
	const utmParams = {
		utmSource: urlParams.get('utm_source') || undefined,
		utmMedium: urlParams.get('utm_medium') || undefined,
		utmCampaign: urlParams.get('utm_campaign') || undefined,
		utmContent: urlParams.get('utm_content') || undefined,
		utmTerm: urlParams.get('utm_term') || undefined,
	};

	return utmParams;
}

/**
 * Create UTM fingerprint for deduplication
 */
export function createUtmFingerprint(utmParams: {
	utmSource?: string;
	utmMedium?: string;
	utmCampaign?: string;
	utmContent?: string;
	utmTerm?: string;
}): string {
	return JSON.stringify({
		source: utmParams.utmSource || '',
		medium: utmParams.utmMedium || '',
		campaign: utmParams.utmCampaign || '',
		content: utmParams.utmContent || '',
		term: utmParams.utmTerm || '',
	});
}

/**
 * Check if we should track this visit (ENHANCED CAMPAIGN-AWARE deduplication logic)
 */
export function shouldTrackVisit(affiliateData: AffiliateData): boolean {
	// If no Game ID, don't track
	if (!affiliateData.gameId) {
		return false;
	}

	// Create UTM fingerprint for this visit
	const currentUtmFingerprint = createUtmFingerprint({
		utmSource: affiliateData.utmSource,
		utmMedium: affiliateData.utmMedium,
		utmCampaign: affiliateData.utmCampaign,
		utmContent: affiliateData.utmContent,
		utmTerm: affiliateData.utmTerm,
	});

	try {
		// Get previously tracked UTM combinations
		const trackedUtms = localStorage.getItem(AFFILIATE_UTM_TRACKING_KEY);
		const trackedUtmMap: Record<string, string> = trackedUtms
			? JSON.parse(trackedUtms)
			: {};

		// Check if we've tracked this EXACT UTM combination recently (within 45 minutes)
		const lastTrackedTime = trackedUtmMap[currentUtmFingerprint];
		if (lastTrackedTime) {
			const timeDiff = Date.now() - new Date(lastTrackedTime).getTime();
			const fortyFiveMinutes = 45 * 60 * 1000; // 45 minutes in milliseconds

			if (timeDiff < fortyFiveMinutes) {
				console.log(
					'🔄 CLIENT: Same UTM campaign already tracked recently, skipping duplicate'
				);
				console.log('📊 UTM fingerprint:', currentUtmFingerprint);
				console.log('📊 Last tracked:', lastTrackedTime);
				console.log(
					'📊 Minutes ago:',
					Math.round(timeDiff / (60 * 1000))
				);
				return false;
			} else {
				console.log(
					'🕒 CLIENT: UTM combination tracked before but outside 45-minute window, allowing'
				);
				console.log(
					'📊 Minutes since last track:',
					Math.round(timeDiff / (60 * 1000))
				);
			}
		}

		// Check for previous UTM parameters to detect campaign changes
		const previousUtmFingerprint = localStorage.getItem(
			'last_utm_fingerprint'
		);
		if (
			previousUtmFingerprint &&
			previousUtmFingerprint !== currentUtmFingerprint
		) {
			console.log(
				'🎯 CLIENT: Different UTM campaign detected, will track new visit'
			);
			console.log('📊 Previous UTM:', previousUtmFingerprint);
			console.log('📊 Current UTM:', currentUtmFingerprint);
		} else if (previousUtmFingerprint === currentUtmFingerprint) {
			console.log('🔄 CLIENT: Same UTM campaign as last visit');
		}

		// Store current UTM fingerprint for next comparison
		localStorage.setItem('last_utm_fingerprint', currentUtmFingerprint);
	} catch (error) {
		console.error('❌ CLIENT: Error checking UTM tracking history:', error);
		// Continue with tracking if we can't check history
	}

	console.log('✅ CLIENT: UTM combination approved for tracking');
	return true;
}

/**
 * Mark UTM combination as tracked
 */
export function markUtmAsTracked(affiliateData: AffiliateData): AffiliateData {
	const utmFingerprint = createUtmFingerprint({
		utmSource: affiliateData.utmSource,
		utmMedium: affiliateData.utmMedium,
		utmCampaign: affiliateData.utmCampaign,
		utmContent: affiliateData.utmContent,
		utmTerm: affiliateData.utmTerm,
	});

	try {
		// Get existing tracked UTM combinations
		const trackedUtms = localStorage.getItem(AFFILIATE_UTM_TRACKING_KEY);
		const trackedUtmMap: Record<string, string> = trackedUtms
			? JSON.parse(trackedUtms)
			: {};

		// Add current UTM combination with timestamp
		trackedUtmMap[utmFingerprint] = new Date().toISOString();

		// Clean up old entries (older than 24 hours)
		const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
		Object.keys(trackedUtmMap).forEach((key) => {
			const trackedTime = new Date(trackedUtmMap[key]).getTime();
			if (trackedTime < oneDayAgo) {
				delete trackedUtmMap[key];
			}
		});

		// Store updated UTM tracking map
		localStorage.setItem(
			AFFILIATE_UTM_TRACKING_KEY,
			JSON.stringify(trackedUtmMap)
		);

		console.log('✅ UTM combination marked as tracked:', utmFingerprint);
	} catch (error) {
		console.error('Error updating UTM tracking status:', error);
	}

	const updatedData = {
		...affiliateData,
		isTracked: true,
		lastTracked: new Date().toISOString(),
	};

	try {
		localStorage.setItem(AFFILIATE_DATA_KEY, JSON.stringify(updatedData));
		localStorage.setItem(AFFILIATE_TRACKED_KEY, 'true');
	} catch (error) {
		console.error('Error updating affiliate tracking status:', error);
	}

	return updatedData;
}

/**
 * Mark session as tracked (DEPRECATED - use markUtmAsTracked for campaign-aware tracking)
 */
export function markSessionAsTracked(
	affiliateData: AffiliateData
): AffiliateData {
	// Use the new UTM-aware tracking method
	return markUtmAsTracked(affiliateData);
}

/**
 * Get or create affiliate session data
 */
export function getAffiliateSession(): AffiliateData | null {
	try {
		const stored = localStorage.getItem(AFFILIATE_DATA_KEY);
		if (stored) {
			const data = JSON.parse(stored) as AffiliateData;
			// Check if session is still valid (24 hours)
			const sessionAge = Date.now() - new Date(data.timestamp).getTime();
			if (sessionAge < 24 * 60 * 60 * 1000) {
				return data;
			} else {
				// Session expired, clear tracking flags
				localStorage.removeItem(AFFILIATE_TRACKED_KEY);
			}
		}
	} catch (error) {
		console.error('Error reading affiliate session:', error);
	}
	return null;
}

/**
 * Create new affiliate session
 */
export function createAffiliateSession(): AffiliateData {
	const gameId = extractAffiliateId();
	const utmParams = extractUtmParameters();
	const sessionId = generateSessionId();

	const affiliateData: AffiliateData = {
		sessionId,
		gameId: gameId || undefined,
		landingPage: window.location.href,
		timestamp: new Date().toISOString(),
		isTracked: false, // NEW: Start as not tracked
		...utmParams,
	};

	// Store in localStorage
	try {
		localStorage.setItem(AFFILIATE_DATA_KEY, JSON.stringify(affiliateData));
		localStorage.setItem(AFFILIATE_SESSION_KEY, sessionId);
		// Clear any previous tracking flag for new session
		localStorage.removeItem(AFFILIATE_TRACKED_KEY);
	} catch (error) {
		console.error('Error storing affiliate session:', error);
	}

	return affiliateData;
}

/**
 * Check if UTM parameters indicate a significant campaign change
 */
export function hasSignificantCampaignChange(
	stored: AffiliateData,
	current: {
		utmSource?: string;
		utmMedium?: string;
		utmCampaign?: string;
		utmContent?: string;
		utmTerm?: string;
	}
): boolean {
	// Create fingerprints for comparison
	const storedFingerprint = createUtmFingerprint({
		utmSource: stored.utmSource,
		utmMedium: stored.utmMedium,
		utmCampaign: stored.utmCampaign,
		utmContent: stored.utmContent,
		utmTerm: stored.utmTerm,
	});

	const currentFingerprint = createUtmFingerprint(current);

	// If fingerprints are different, it's a campaign change
	if (storedFingerprint !== currentFingerprint) {
		console.log(
			'🎯 CAMPAIGN CHANGE DETECTED - Creating new session for different UTM parameters'
		);
		console.log('📊 Previous UTM fingerprint:', storedFingerprint);
		console.log('📊 Current UTM fingerprint:', currentFingerprint);
		return true;
	}

	return false;
}

/**
 * Initialize affiliate tracking on page load (ENHANCED with campaign-aware session management)
 */
export function initializeAffiliateTracking(): AffiliateData {
	// Check if we already have a session
	let affiliateData = getAffiliateSession();

	// Get current parameters
	const currentGameId = extractAffiliateId();
	const currentUtmParams = extractUtmParameters();

	// Create new session if:
	// 1. No existing session
	// 2. Different Game ID
	// 3. Significant UTM campaign change for same Game ID
	if (
		!affiliateData ||
		(currentGameId && currentGameId !== affiliateData.gameId) ||
		(affiliateData &&
			hasSignificantCampaignChange(affiliateData, currentUtmParams))
	) {
		console.log('🆕 Creating new affiliate session');
		if (affiliateData) {
			console.log('📊 Reason: UTM campaign change detected');
		}
		affiliateData = createAffiliateSession();
	} else {
		console.log(
			'♻️ Reusing existing affiliate session:',
			affiliateData.sessionId
		);
	}

	return affiliateData;
}

/**
 * Track affiliate visit (call this on app initialization)
 * NOW WITH CAMPAIGN-AWARE DEDUPLICATION LOGIC
 */
export async function trackAffiliateVisit(
	trackVisit: (args: {
		sessionId: string;
		gameId: string;
		landingPage: string;
		visitorIp?: string;
		userAgent?: string;
		utmSource?: string;
		utmMedium?: string;
		utmCampaign?: string;
		utmContent?: string;
		utmTerm?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	}) => Promise<string | any> // Updated to handle new return types
) {
	const affiliateData = initializeAffiliateTracking();

	// Check if we should track this visit (campaign-aware client-side deduplication)
	if (!shouldTrackVisit(affiliateData)) {
		return affiliateData;
	}

	console.log(
		'✅ Tracking new affiliate visit for session:',
		affiliateData.sessionId
	);

	// Only track if there's a Game ID and we haven't tracked this UTM combination recently
	if (affiliateData.gameId) {
		const trackingArgs = {
			sessionId: affiliateData.sessionId,
			gameId: affiliateData.gameId,
			landingPage: affiliateData.landingPage,
			visitorIp: undefined, // Will be determined server-side if needed
			userAgent: navigator.userAgent,
			utmSource: affiliateData.utmSource,
			utmMedium: affiliateData.utmMedium,
			utmCampaign: affiliateData.utmCampaign,
			utmContent: affiliateData.utmContent,
			utmTerm: affiliateData.utmTerm,
		};

		try {
			const result = await trackVisit(trackingArgs);

			// Handle different response types from server
			if (typeof result === 'string') {
				if (result.startsWith('duplicate_')) {
					console.log(
						'🔄 SERVER: Duplicate tracking prevented:',
						result
					);
					// Still mark as tracked since server handled it
					markUtmAsTracked(affiliateData);
					return affiliateData;
				}
			} else {
				// Successful tracking (got tracking ID)
				markUtmAsTracked(affiliateData);
				console.log(
					'🎯 Affiliate visit tracking successful, ID:',
					result
				);
			}
		} catch (error) {
			console.error('❌ Error tracking affiliate visit:', error);
			// Don't mark as tracked if there was an error
		}
	}

	return affiliateData;
}

/**
 * Convert affiliate session when user signs up
 */
export async function convertAffiliateSession(
	convertTracking: (args: {
		sessionId: string;
		conversionUserId: Id<'users'>;
		conversionType: string;
		conversionValue?: number;
	}) => Promise<string | null>,
	userId: Id<'users'>,
	conversionType: string = 'signup',
	conversionValue?: number
) {
	const affiliateData = getAffiliateSession();

	if (affiliateData && affiliateData.gameId) {
		try {
			await convertTracking({
				sessionId: affiliateData.sessionId,
				conversionUserId: userId,
				conversionType,
				conversionValue,
			});
		} catch (error) {
			console.error('Error converting affiliate session:', error);
		}
	}
}

/**
 * Clear affiliate session (call after conversion or when no longer needed)
 */
export function clearAffiliateSession() {
	try {
		localStorage.removeItem(AFFILIATE_DATA_KEY);
		localStorage.removeItem(AFFILIATE_SESSION_KEY);
		localStorage.removeItem(AFFILIATE_TRACKED_KEY); // NEW: Clear tracking flag too
	} catch (error) {
		console.error('Error clearing affiliate session:', error);
	}
}

/**
 * Force refresh tracking (for testing or special cases)
 */
export function forceRefreshTracking() {
	try {
		localStorage.removeItem(AFFILIATE_TRACKED_KEY);
		localStorage.removeItem(AFFILIATE_UTM_TRACKING_KEY); // NEW: Clear UTM tracking history
		localStorage.removeItem('last_utm_fingerprint'); // NEW: Clear last UTM fingerprint
		console.log(
			'🔄 All tracking flags cleared - next page load will track again'
		);
	} catch (error) {
		console.error('Error clearing tracking flags:', error);
	}
}

/**
 * Hook for affiliate tracking
 */
export function useAffiliateTracking() {
	const trackVisit = useMutation(api.affiliate.trackAffiliateVisit);
	const convertTracking = useMutation(api.affiliate.convertAffiliateTracking);

	const initializeTracking = () => trackAffiliateVisit(trackVisit);
	const convertSession = (
		userId: Id<'users'>,
		conversionType?: string,
		conversionValue?: number
	) =>
		convertAffiliateSession(
			convertTracking,
			userId,
			conversionType,
			conversionValue
		);

	return {
		initializeTracking,
		convertSession,
		getSession: getAffiliateSession,
		clearSession: clearAffiliateSession,
		forceRefresh: forceRefreshTracking, // NEW: For debugging/testing
	};
}
